import { Enum, game, _decorator } from 'cc';
import { DEV, EDITOR } from 'cc/env';
import DOMElementAdapter from './DOMElementAdapter';

export enum HTMLElementTagName {
    a = 'a',
    abbr = 'abbr',
    address = 'address',
    area = 'area',
    article = 'article',
    aside = 'aside',
    audio = 'audio',
    b = 'b',
    base = 'base',
    bdi = 'bdi',
    bdo = 'bdo',
    blockquote = 'blockquote',
    body = 'body',
    br = 'br',
    button = 'button',
    canvas = 'canvas',
    caption = 'caption',
    cite = 'cite',
    code = 'code',
    col = 'col',
    colgroup = 'colgroup',
    data = 'data',
    datalist = 'datalist',
    dd = 'dd',
    del = 'del',
    details = 'details',
    dfn = 'dfn',
    dialog = 'dialog',
    dir = 'dir',
    div = 'div',
    dl = 'dl',
    dt = 'dt',
    em = 'em',
    embed = 'embed',
    fieldset = 'fieldset',
    figcaption = 'figcaption',
    figure = 'figure',
    font = 'font',
    footer = 'footer',
    form = 'form',
    frame = 'frame',
    frameset = 'frameset',
    h1 = 'h1',
    h2 = 'h2',
    h3 = 'h3',
    h4 = 'h4',
    h5 = 'h5',
    h6 = 'h6',
    head = 'head',
    header = 'header',
    hgroup = 'hgroup',
    hr = 'hr',
    html = 'html',
    i = 'i',
    iframe = 'iframe',
    img = 'img',
    input = 'input',
    ins = 'ins',
    kbd = 'kbd',
    label = 'label',
    legend = 'legend',
    li = 'li',
    link = 'link',
    main = 'main',
    map = 'map',
    mark = 'mark',
    marquee = 'marquee',
    menu = 'menu',
    meta = 'meta',
    meter = 'meter',
    nav = 'nav',
    noscript = 'noscript',
    object = 'object',
    ol = 'ol',
    optgroup = 'optgroup',
    option = 'option',
    output = 'output',
    p = 'p',
    param = 'param',
    picture = 'picture',
    pre = 'pre',
    progress = 'progress',
    q = 'q',
    rp = 'rp',
    rt = 'rt',
    ruby = 'ruby',
    s = 's',
    samp = 'samp',
    script = 'script',
    section = 'section',
    select = 'select',
    slot = 'slot',
    small = 'small',
    source = 'source',
    span = 'span',
    strong = 'strong',
    style = 'style',
    sub = 'sub',
    summary = 'summary',
    sup = 'sup',
    table = 'table',
    tbody = 'tbody',
    td = 'td',
    template = 'template',
    textarea = 'textarea',
    tfoot = 'tfoot',
    th = 'th',
    thead = 'thead',
    time = 'time',
    title = 'title',
    tr = 'tr',
    track = 'track',
    u = 'u',
    ul = 'ul',
    var = 'var',
    video = 'video',
    wbr = 'wbr',
}

const { ccclass, property } = _decorator;

/**
 * A class that creates and manages a DOM element with a specific tag name and ID.
 * @template T - The type of HTML element this class manages
 */
@ccclass('DOMElementGenerator')
export default class DOMElementGenerator extends DOMElementAdapter<HTMLElement> {
    @property({ formerlySerializedAs: 'elementTagName' })
    protected _elementTagName: HTMLElementTagName = HTMLElementTagName.div;

    @property({ formerlySerializedAs: 'elementID' })
    private _elementID: string = '';

    @property
    private _addToGameContainer: boolean = true;

    @property({ type: Enum(HTMLElementTagName), tooltip: DEV && 'The tag name of the element, used when the target element is not found in DOM tree' })
    public get elementTagName() {
        return this._elementTagName;
    }
    public set elementTagName(value: HTMLElementTagName) {
        if (this._elementTagName === value) {
            return;
        }
        this._elementTagName = value;
        if (!EDITOR && this._element) {
            // remove old element and create a new one
            this.removeElement();
            this.initElement();
        }
    }

    @property({ tooltip: DEV && 'The id of the element, used when the target element is not found in DOM tree' })
    public get elementID() {
        return this._elementID;
    }
    public set elementID(value: string) {
        if (this._elementID === value) {
            return;
        }
        this._elementID = value;
        if (!EDITOR && this._element) {
            // remove old element and create a new one
            this.removeElement();
            this.initElement();
        }
    }

    @property
    public get addToGameContainer() {
        return this._addToGameContainer;
    }
    public set addToGameContainer(value: boolean) {
        if (this._addToGameContainer === value) {
            return;
        }
        this._addToGameContainer = value;
        if (!EDITOR && this._element) {
            if (value) {
                game.container.appendChild(this._element);
            } else {
                game.container.removeChild(this._element);
            }
        }
    }

    protected findElement(): HTMLElement {
        let element: HTMLElement;
        if (this.elementID) {
            element = document.getElementById(this.elementID);
        }
        // if element not exists, create it
        if (!element) {
            element = document.createElement(this.elementTagName);
            element.id = this.elementID;
        }
        if (this.addToGameContainer) {
            game.container!.appendChild(element);
        }
        return element;
    }
}
